import React from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { Button } from '@Comp';
import OnboardingSidebar from './OnboardingSidebar';
import { Loading } from '../../shared/components';
import { Snackbar } from '@material-ui/core';
import { isMobile } from 'is-mobile';
import images from '@assets';
import { User } from '../../services/requests';
import onboardingStore from '../../stores/onboardingStore';
import OnboardingService from '../../services/onboarding.service';
import './FundingInformation.scss';

const FINANCE_TYPES = [
    {
        id: 'invoice',
        label: 'Invoice Finance',
        title: 'Invoice Finance',
        description: 'Get a loan of up to 80% of the value of your selected invoices, with the remaining 20% paid to you when your customer pays the invoices.'
    },
    {
        id: 'purchase_order',
        label: 'Purchase Order Finance',
        title: 'Purchase Order Finance',
        description: 'Finance your purchase orders and pay suppliers while waiting for customer payment.'
    },
    {
        id: 'contract',
        label: 'Contract Finance',
        title: 'Contract Finance',
        description: 'Get funding based on your confirmed contracts and future revenue streams.'
    }
];

const FUNDING_DURATIONS = [
    { value: '1 month', label: '1 month' },
    { value: '3 months', label: '3 months' },
    { value: '6 months', label: '6 months' },
    { value: '12 months', label: '12 months' }
];

@inject('commonStore')
@withRouter
@observer
export default class FundingInformation extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedFinanceType: 'invoice',
            fundingRequired: '',
            invoiceValue: '',
            fundingDuration: '3 months',
            showHowItWorksModal: false,
            showSuccessModal: false,
            showExitConfirmation: false,
            isMobileScreen: window.innerWidth < 480
        };
    }

    componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        this.handleResize();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize = () => {
        const newIsMobileScreen = window.innerWidth < 480;
        if (newIsMobileScreen !== this.state.isMobileScreen) {
            this.setState({
                isMobileScreen: newIsMobileScreen,
            });
        }
    };

    toggleNav = e => {
        e.preventDefault();
        let { commonStore } = this.props;
        commonStore.setNavMini(!commonStore.navMini);
    };

    hideNav = e => {
        e.stopPropagation();
        e.preventDefault();
        let { commonStore } = this.props;
        commonStore.setNavMini(false);
    };

    close = () => {
        this.props.commonStore.open = false;
    };

    navigateHome = () => {
        // Show confirmation dialog before leaving
        this.setState({ showExitConfirmation: true });
    };

    handleCancelExit = () => {
        this.setState({ showExitConfirmation: false });
    };

    handleConfirmExit = async () => {
        const { commonStore, history } = this.props;

        try {
            // Close the modal first
            this.setState({ showExitConfirmation: false });

            // Show loading
            commonStore.setLoading(true);

            // Get all saved onboarding data (completed steps only)
            const onboardingData = onboardingStore.getOnboardingData();

            const profileId = commonStore.businessProfile?.id;

            // Complete onboarding with whatever data has been saved
            const result = await OnboardingService.completeOnboarding(onboardingData, profileId);

            if (result.success) {
                // Update business profile if needed
                if (result.profileId && !profileId) {
                    await commonStore.getBusinessProfile(result.profileId);
                }

                // Clear onboarding data from storage
                onboardingStore.clearOnCompletion();

                // Show success message
                commonStore.setMsg('Your progress has been saved. You can continue onboarding anytime.');

                // Navigate to home
                history.push('/home');
            }
        } catch (error) {
            console.error('Error completing onboarding:', error);

            // Even if there's an error, allow navigation (non-blocking)
            commonStore.setMsg('Your progress has been saved locally.');

            // Navigate to home anyway
            history.push('/home');
        } finally {
            commonStore.setLoading(false);
        }
    };

    handleFinanceTypeChange = (typeId) => {
        this.setState({ selectedFinanceType: typeId });
    };

    handleCurrencyInput = (field, value) => {
        // Remove formatting, store raw number
        const numericValue = value.replace(/[^0-9.]/g, '');
        // Prevent multiple decimal points
        const parts = numericValue.split('.');
        const cleanValue = parts.length > 2
            ? parts[0] + '.' + parts.slice(1).join('')
            : numericValue;
        this.setState({ [field]: cleanValue });
    };

    formatCurrency = (value) => {
        // Format number with commas: 350000 → 350,000.00
        if (!value) return '';
        const num = parseFloat(value);
        if (isNaN(num)) return '';
        return num.toLocaleString('en-ZA', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    handleDurationChange = (e) => {
        this.setState({ fundingDuration: e.target.value });
    };

    isFormValid = () => {
        const { fundingRequired, invoiceValue, fundingDuration } = this.state;
        return (
            parseFloat(fundingRequired) > 0 &&
            parseFloat(invoiceValue) > 0 &&
            fundingDuration
        );
    };

    handleHowItWorks = () => {
        this.setState({ showHowItWorksModal: true });
    };

    updateOnboardingStatus = async () => {
        try {
            const userId = this.props.commonStore.user._id || this.props.commonStore.user.id;
            if (userId) {
                await User.edit(userId, { onboardingCompleted: true });

                // Update user in store
                const updatedUser = { ...this.props.commonStore.user, onboardingCompleted: true };
                this.props.commonStore.setUser(updatedUser);
            }
        } catch (error) {
            console.error('Error updating onboarding status:', error);
            // Continue navigation even if API fails
        }
    };

    handleFinishApplication = async () => {
        if (!this.isFormValid()) return;
        this.setState({ showSuccessModal: true });
    };

    handleGoToDashboard = async () => {
        this.setState({ showSuccessModal: false });

        try {
            // Update onboarding completed flag
            await this.updateOnboardingStatus();
        } catch (error) {
            console.error('Error in handleGoToDashboard:', error);
        } finally {
            // Navigate to dashboard regardless of API success/failure
            this.props.history.push('/home');
        }
    };

    handleSkip = async () => {
        try {
            // Update onboarding completed flag
            await this.updateOnboardingStatus();
        } catch (error) {
            console.error('Error in handleSkip:', error);
        } finally {
            // Navigate to dashboard regardless of API success/failure
            this.props.history.push('/home');
        }
    };

    closeHowItWorksModal = () => {
        this.setState({ showHowItWorksModal: false });
    };

    closeSuccessModal = () => {
        this.setState({ showSuccessModal: false });
    };

    render() {
        const { commonStore } = this.props;
        const { navMini, open, msg } = commonStore;
        const {
            selectedFinanceType,
            fundingRequired,
            invoiceValue,
            fundingDuration,
            showHowItWorksModal,
            showSuccessModal,
            showExitConfirmation
        } = this.state;

        const selectedType = FINANCE_TYPES.find(type => type.id === selectedFinanceType);

        return (
            <div className='app-wrapper funding-information-layout'>
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={open}
                    autoHideDuration={5000}
                    message={msg}
                    onClose={this.close}
                />
                <Loading />
                <OnboardingSidebar mini={navMini} />
                <div
                    className={`content-container ${navMini ? 'full' : ''} ${
                        isMobile() ? 'mobile' : ''
                    }`}
                >
                    {/* dropshadow for mobile nav triggering */}
                    <div
                        className='menu-dropshadow'
                        style={{ display: navMini ? 'block' : 'none' }}
                        onClick={this.hideNav}
                    ></div>

                    {/* Top Navigation Bar */}
                    <header className="funding-header-nav">
                        <div className="header-left">
                            <img
                                src={images.menu_white}
                                alt='menu'
                                onClick={this.toggleNav}
                                className="menu-icon"
                            />
                        </div>
                        <div className="header-right">
                            <button
                                className="home-button"
                                onClick={this.navigateHome}
                            >
                                Home
                            </button>
                        </div>
                    </header>

                    {/* Main Content Area */}
                    <div className="funding-main-content">
                        <div className="funding-container">
                            {/* Left Panel - Finance Type Selector */}
                            <div className="finance-type-panel">
                                <h3 className="panel-title">Select Finance Type</h3>
                                <div className="finance-type-list">
                                    {FINANCE_TYPES.map(type => (
                                        <button
                                            key={type.id}
                                            className={`finance-type-button ${
                                                selectedFinanceType === type.id ? 'active' : ''
                                            }`}
                                            onClick={() => this.handleFinanceTypeChange(type.id)}
                                        >
                                            {type.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Right Panel - Dynamic Form */}
                            <div className="finance-form-panel">
                                <div className="form-header">
                                    <h2 className="form-title">{selectedType.title}</h2>
                                    <p className="form-description">{selectedType.description}</p>
                                </div>

                                <div className="form-content">
                                    {/* Funding Required */}
                                    <div className="form-group">
                                        <label className="form-label">
                                            How much funding do you require?
                                        </label>
                                        <div className="currency-input-wrapper">
                                            <span className="currency-prefix">R</span>
                                            <input
                                                type="text"
                                                className="currency-input"
                                                value={this.formatCurrency(fundingRequired)}
                                                onChange={(e) => this.handleCurrencyInput('fundingRequired', e.target.value)}
                                                placeholder="350,000.00"
                                            />
                                        </div>
                                    </div>

                                    {/* Invoice Value */}
                                    <div className="form-group">
                                        <label className="form-label">
                                            What is the value of the invoice?
                                        </label>
                                        <div className="currency-input-wrapper">
                                            <span className="currency-prefix">R</span>
                                            <input
                                                type="text"
                                                className="currency-input"
                                                value={this.formatCurrency(invoiceValue)}
                                                onChange={(e) => this.handleCurrencyInput('invoiceValue', e.target.value)}
                                                placeholder="350,000.00"
                                            />
                                        </div>
                                    </div>

                                    {/* Funding Duration */}
                                    <div className="form-group">
                                        <label className="form-label">
                                            How long do you need the funding for?
                                        </label>
                                        <select
                                            className="duration-select"
                                            value={fundingDuration}
                                            onChange={this.handleDurationChange}
                                        >
                                            {FUNDING_DURATIONS.map(duration => (
                                                <option key={duration.value} value={duration.value}>
                                                    {duration.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="form-actions">
                                        <Button
                                            color="secondary"
                                            outline
                                            onClick={this.handleSkip}
                                            className="skip-btn"
                                        >
                                            Skip
                                        </Button>
                                        <Button
                                            color="secondary"
                                            outline
                                            onClick={this.handleHowItWorks}
                                            className="how-it-works-btn"
                                        >
                                            How it works
                                        </Button>
                                        <Button
                                            color="primary"
                                            onClick={this.handleFinishApplication}
                                            disabled={!this.isFormValid()}
                                            className="finish-application-btn"
                                        >
                                            Finish application
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* How It Works Modal */}
                <Modal
                    show={showHowItWorksModal}
                    onHide={this.closeHowItWorksModal}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>How {selectedType.title} Works</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Content coming soon...</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button color="secondary" onClick={this.closeHowItWorksModal}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Success Modal */}
                <Modal
                    show={showSuccessModal}
                    onHide={this.closeSuccessModal}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Application Submitted Successfully!</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Your funding application has been received.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button color="primary" onClick={this.handleGoToDashboard}>
                            Go to Dashboard
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Exit Confirmation Modal */}
                <Modal show={showExitConfirmation} onHide={this.handleCancelExit} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Exit Onboarding?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to exit onboarding? Your progress will be saved.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.handleCancelExit}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={this.handleConfirmExit}>
                            Confirm
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}
